import { Plan } from '@models/membership/Plan'
import { Subscription as SubscriptionModel } from '@models/membership/Subscription'
import { changeSubscriptionPlan } from '@services/membership/SubscriptionService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { selectMembershipProfilesByIds } from '@store/membership/MembershipProfileReducer'
import { getPlans, selectPlans } from '@store/membership/PlanReducer'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { requiredRule } from '@utils/rules'
import { Alert, App, Button, Form, Modal, Select } from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useState } from 'react'

type Props = {
  subscription: SubscriptionModel
  onChange?: () => void | Promise<unknown>
}

/**
 * Same rule as the membership signup form: the birth date must fall between
 * today minus `maxAge` years and today minus `minAge` years.
 */
const isOutsideAgeRange = (birthDate: string, plan: Plan): boolean => {
  const birth = dayjs(birthDate)
  if (
    plan.maxAge &&
    birth.isBefore(dayjs().subtract(plan.maxAge, 'year'), 'day')
  )
    return true
  if (
    plan.minAge &&
    birth.isAfter(dayjs().subtract(plan.minAge, 'year'), 'day')
  )
    return true
  return false
}

const formatAgeRange = (plan: Plan): string => {
  if (plan.minAge && plan.maxAge) return `${plan.minAge}–${plan.maxAge}`
  if (plan.minAge) return `${plan.minAge}+`
  return `up to ${plan.maxAge}`
}

const plansQuery = {
  ...initialQuery,
  pagination: { ...initialQuery.pagination, current: 1, pageSize: 1000 },
}

/**
 * Moves an existing subscription to another plan of the same program, for
 * the same number of members.
 *
 * The API refuses (422, with a readable message) when the subscription is not
 * active / to_cancel, when the plan is the current one or belongs to another
 * program, or when the attached coupon is not valid for the new plan. Perks
 * and venue access change immediately; the new price only applies at the next
 * renewal, with no proration.
 *
 * Plans come from the plan store (one full fetch) and are filtered to the
 * current plan's program client-side: the plan index has no program filter.
 * Refresh goes through `onChange`, like AttachCoupon.
 */
const ChangePlan: FC<Props> = ({ subscription, onChange }) => {
  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm<{ plan: number }>()
  const plans = useAppSelector(selectPlans())

  const currentPlan = subscription.plan
  const programId = currentPlan?.program?.id

  useEffect(() => {
    if (open) dispatch(getPlans(plansQuery))
  }, [open])

  const members = useAppSelector(
    selectMembershipProfilesByIds(subscription.membershipProfiles ?? [])
  )
  const selectedPlanId = Form.useWatch('plan', form)
  const selectedPlan = plans.find((plan: Plan) => plan.id === selectedPlanId)

  // Allowed on purpose (staff may have a reason), but never silently.
  const membersOutsideAgeRange = selectedPlan
    ? members.filter(
        (member) =>
          member.birthDate && isOutsideAgeRange(member.birthDate, selectedPlan)
      )
    : []

  const options = plans
    .filter(
      (plan: Plan) =>
        plan.program?.id === programId &&
        plan.id !== currentPlan?.id &&
        // A couple plan only moves to a couple plan, an individual one to an
        // individual one (the API refuses any other change).
        plan.membersLimit === currentPlan?.membersLimit
    )
    .map((plan: Plan) => ({ value: plan.id, label: plan.en.name }))

  const onSubmit = async () => {
    const { plan } = await form.validateFields()
    setLoading(true)
    try {
      await changeSubscriptionPlan(subscription.id, plan)
      await onChange?.()
      message.success('Plan changed')
      setOpen(false)
      form.resetFields()
    } catch (error: any) {
      // 422: the API explains in English why the change is refused
      message.error(
        error?.response?.data?.data?.message ??
          error?.response?.data?.message ??
          'The plan could not be changed'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        Change plan
      </Button>
      <Modal
        title="Change plan"
        open={open}
        onOk={onSubmit}
        confirmLoading={loading}
        onCancel={() => setOpen(false)}
        okText={
          membersOutsideAgeRange.length > 0
            ? 'Change plan anyway'
            : 'Change plan'
        }
        okButtonProps={{ danger: membersOutsideAgeRange.length > 0 }}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="The new plan's perks and venue access apply immediately. Its price applies from the next renewal, with no proration."
        />
        {selectedPlan && membersOutsideAgeRange.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title={`Outside this plan's age range (${formatAgeRange(selectedPlan)})`}
            description={
              <>
                {membersOutsideAgeRange.map((member) => (
                  <div key={member.id}>
                    {`${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()}{' '}
                    is {dayjs().diff(dayjs(member.birthDate), 'year')} years
                    old.
                  </div>
                ))}
                You can still change the plan.
              </>
            }
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item name="plan" label="New plan" rules={[requiredRule()]}>
            <Select
              options={options}
              placeholder="Select a plan"
              notFoundContent="No other plan of this program for the same number of members"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ChangePlan
